import {
  DEFAULT_TIMEZONE,
  DEFAULT_TIMEZONE_ABBR,
  EVENT_TYPES,
  RANGE,
} from "../influencerEvent/constants";

const DASH = "—";
const UNTITLED = "Untitled Event";
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

const partsFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const isKnownTimeZone = (timeZone) => {
  try {
    partsFormatter(timeZone);
    return true;
  } catch {
    return false;
  }
};

const eventTimeZone = ({ timeZone }) =>
  timeZone && isKnownTimeZone(timeZone) ? timeZone : DEFAULT_TIMEZONE;

const eventTimeZoneAbbr = ({ timeZoneAbbr }) =>
  timeZoneAbbr || DEFAULT_TIMEZONE_ABBR;

// Assembled from parts because ICU puts a narrow no-break space before AM/PM.
const toDateParts = (timeZone) => (date) => {
  const parts = Object.fromEntries(
    partsFormatter(timeZone)
      .formatToParts(date)
      .map(({ type, value }) => [type, value]),
  );

  return {
    month: parts.month,
    day: Number(parts.day),
    year: parts.year,
    time: `${parts.hour}:${parts.minute} ${parts.dayPeriod}`,
  };
};

const parseInstant = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const parseCalendarDate = (text) => {
  const [, year, month, day] = CALENDAR_DATE.exec(text ?? "") ?? [];
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const isRealDate =
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day);

  return year && isRealDate ? date : null;
};

const mapNullable = (fn) => (value) => value === null ? null : fn(value);

const isRange = (event) => event.dateType === RANGE;

const startParts = (event) =>
  isRange(event)
    ? mapNullable(toDateParts("UTC"))(parseCalendarDate(event.eventStartDate))
    : mapNullable(toDateParts(eventTimeZone(event)))(
        parseInstant(event.nextEventTime),
      );

const endParts = (event) =>
  mapNullable(toDateParts("UTC"))(parseCalendarDate(event.eventEndDate));

const eventTitle = (event) => event.eventTitle || UNTITLED;

const fullDate = ({ month, day, year }) => `${month} ${day}, ${year}`;

const withDetail = (type, detail) => [type, detail].filter(Boolean).join(" · ");

const rangeSubtitle = (event) => {
  const start = startParts(event);
  const end = endParts(event);

  return start && end
    ? withDetail(
        event.eventType,
        `${start.month} ${start.day} – ${end.month} ${end.day}`,
      )
    : event.eventType;
};

const dayAndTimeSubtitle = (event) => {
  const start = startParts(event);

  return event.hasTime && start
    ? withDetail(event.eventType, `${start.time} ${eventTimeZoneAbbr(event)}`)
    : event.eventType;
};

export const formatEventEntry = (event) => {
  const start = startParts(event);

  return {
    month: start?.month ?? DASH,
    day: start?.day ?? DASH,
    title: eventTitle(event),
    subtitle: isRange(event) ? rangeSubtitle(event) : dayAndTimeSubtitle(event),
  };
};

const occurrenceCount = (count) =>
  `${count} past ${count === 1 ? "occurrence" : "occurrences"}`;

export const formatSeriesEntry = ({ latest, pastOccurrenceCount }) => {
  const entry = formatEventEntry(latest);

  return latest.isRecurring
    ? {
        ...entry,
        subtitle: withDetail(
          entry.subtitle,
          occurrenceCount(pastOccurrenceCount),
        ),
      }
    : entry;
};

const rangeDateTime = (event) => {
  const start = startParts(event);
  const end = endParts(event);

  return [start, end].filter(Boolean).map(fullDate).join(" – ");
};

const dayAndTimeDateTime = (event) => {
  const start = startParts(event);

  return event.hasTime
    ? `${fullDate(start)} · ${start.time} ${eventTimeZoneAbbr(event)}`
    : fullDate(start);
};

export const formatEventDateTime = (event) =>
  startParts(event) === null
    ? DASH
    : isRange(event)
    ? rangeDateTime(event)
    : dayAndTimeDateTime(event);

const optionalRow = (label, value) => (value ? [{ label, value }] : []);

const HTTP_URL = /^https?:\/\//i;

const urlRow = (url) =>
  HTTP_URL.test(url ?? "")
    ? [{ label: "URL", value: url, href: url }]
    : optionalRow("URL", url);

export const toEventSummary = (event) => [
  { label: "Title", value: eventTitle(event) },
  { label: "Type", value: event.eventType || DASH },
  { label: "Date", value: formatEventDateTime(event) },
  ...optionalRow("Platform", event.platform),
  ...urlRow(event.eventUrl),
  { label: "Recurring", value: event.isRecurring ? "Yes" : "No" },
  ...optionalRow("Description", event.eventDescription),
];

export const isOffCamera = (event) =>
  event.eventType === EVENT_TYPES.NON_LIVE_SIGNING;

export const eventPage = ({ isPast, influencerEventId }) =>
  isPast ? `events/${influencerEventId}` : `events/${influencerEventId}/edit`;
